import React, { useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { fetchAddresses, createAddress, updateAddress, deleteAddress, setDefaultAddress } from '../../../lib/addresses';

const AddressBook = () => {
  const [addresses, setAddresses] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAddresses()
      .then((rows) => { if (!cancelled) setAddresses(rows); })
      .catch(() => { if (!cancelled) setLoadError('Could not load addresses. Refresh to try again.'); });
    return () => { cancelled = true; };
  }, []);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'home',
    name: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: ''
  });

  const addressTypes = [
    { id: 'home', label: 'Home', icon: 'Home' },
    { id: 'work', label: 'Work', icon: 'Building' },
    { id: 'other', label: 'Other', icon: 'MapPin' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAddress = () => {
    setFormData({
      type: 'home',
      name: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
      phone: ''
    });
    setEditingId(null);
    setShowAddForm(true);
  };

  const handleEditAddress = (address) => {
    setFormData(address);
    setEditingId(address?.id);
    setShowAddForm(true);
  };

  const handleSaveAddress = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      if (editingId) {
        const res = await updateAddress(editingId, formData);
        if (res.error) { setSaveError(res.error); return; }
        setAddresses(prev => (prev || []).map(addr => addr?.id === editingId ? res.address : addr));
      } else {
        const res = await createAddress(formData);
        if (res.error) { setSaveError(res.error); return; }
        setAddresses(prev => [...(prev || []), res.address]);
      }
      setShowAddForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    const res = await deleteAddress(id);
    if (res.error) { setSaveError(res.error); return; }
    // Refetch so the auto-promote-default behavior on backend is reflected here too
    const rows = await fetchAddresses();
    setAddresses(rows);
  };

  const handleSetDefault = async (id) => {
    setAddresses(prev => (prev || []).map(addr => ({ ...addr, isDefault: addr?.id === id })));
    const res = await setDefaultAddress(id);
    if (res.error) {
      setSaveError(res.error);
      const rows = await fetchAddresses();
      setAddresses(rows);
    }
  };

  const getAddressTypeIcon = (type) => {
    return addressTypes?.find(t => t?.id === type)?.icon || 'MapPin';
  };

  const getAddressTypeLabel = (type) => {
    return addressTypes?.find(t => t?.id === type)?.label || 'Other';
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Address Book
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your shipping and billing addresses
              </p>
            </div>
            <Button
              onClick={handleAddAddress}
              iconName="Plus"
              iconPosition="left"
            >
              Add Address
            </Button>
          </div>
        </div>

        {/* Address Form Modal */}
        {showAddForm && (
          <div className="px-6 py-6 bg-accent/10 border-b border-border">
            <div className="max-w-2xl">
              <h3 className="text-lg font-medium text-foreground mb-4">
                {editingId ? 'Edit Address' : 'Add New Address'}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Address Type */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Address Type
                  </label>
                  <div className="flex space-x-4">
                    {addressTypes?.map((type) => (
                      <button
                        key={type?.id}
                        onClick={() => handleInputChange('type', type?.id)}
                        className={cn(
                          "flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors",
                          formData?.type === type?.id
                            ? "border-primary bg-primary/10 text-primary" :"border-input hover:bg-accent"
                        )}
                      >
                        <Icon name={type?.icon} size={16} />
                        <span className="text-sm">{type?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Full Name */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name
                  </label>
                  <Input
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    placeholder="Enter full name"
                  />
                </div>

                {/* Address */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Street Address
                  </label>
                  <Input
                    value={formData?.address}
                    onChange={(e) => handleInputChange('address', e?.target?.value)}
                    placeholder="123 Main Street"
                  />
                </div>

                {/* Address 2 */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Apartment, Suite, etc. (Optional)
                  </label>
                  <Input
                    value={formData?.address2}
                    onChange={(e) => handleInputChange('address2', e?.target?.value)}
                    placeholder="Apt 4B, Suite 200, etc."
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    City
                  </label>
                  <Input
                    value={formData?.city}
                    onChange={(e) => handleInputChange('city', e?.target?.value)}
                    placeholder="Enter city"
                  />
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    State/Province
                  </label>
                  <Input
                    value={formData?.state}
                    onChange={(e) => handleInputChange('state', e?.target?.value)}
                    placeholder="NY"
                  />
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    ZIP/Postal Code
                  </label>
                  <Input
                    value={formData?.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                    placeholder="10001"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAddress}
                  iconName="Check"
                  iconPosition="left"
                >
                  {editingId ? 'Update Address' : 'Add Address'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Addresses List */}
        <div className="p-6">
          {addresses?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {addresses?.map((address) => (
                <div
                  key={address?.id}
                  className={cn(
                    "relative p-4 rounded-lg border transition-colors",
                    address?.isDefault 
                      ? "border-primary bg-primary/5" :"border-border hover:border-primary/50"
                  )}
                >
                  {/* Default Badge */}
                  {address?.isDefault && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                        Default
                      </span>
                    </div>
                  )}

                  {/* Address Type & Icon */}
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon 
                      name={getAddressTypeIcon(address?.type)} 
                      size={16} 
                      className="text-primary"
                    />
                    <span className="text-sm font-medium text-primary">
                      {getAddressTypeLabel(address?.type)}
                    </span>
                  </div>

                  {/* Address Details */}
                  <div className="space-y-1 mb-4">
                    <p className="font-medium text-foreground">{address?.name}</p>
                    <p className="text-sm text-muted-foreground">{address?.address}</p>
                    {address?.address2 && (
                      <p className="text-sm text-muted-foreground">{address?.address2}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {address?.city}, {address?.state} {address?.zipCode}
                    </p>
                    <p className="text-sm text-muted-foreground">{address?.country}</p>
                    {address?.phone && (
                      <p className="text-sm text-muted-foreground">{address?.phone}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {!address?.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(address?.id)}
                      >
                        Set as Default
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAddress(address)}
                      iconName="Edit"
                    >
                      Edit
                    </Button>
                    {!address?.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAddress(address?.id)}
                        iconName="Trash"
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="MapPin" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                No addresses saved
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Add your first address to make checkout faster
              </p>
              <Button onClick={handleAddAddress}>
                Add Your First Address
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressBook;